import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import Shepherd from 'shepherd.js';
import {translate} from '@jsverse/transloco';
import {TourEvent} from 'src/app/models/tour/tour-event';
import {ScrollService} from './scroll.service';
import {TourStepSetting} from 'src/app/models/tour/tour-step-settings';
import {EnterpriseTourStep} from 'src/app/models/tour/tour-step-enums/enterprise-tour-step';
import {TourConfig} from 'src/assets/config/tour/tour-config';

@Injectable({
  providedIn: 'root'
})
export class TourService {

  tourSubject = new Subject<TourEvent>();
  viewedSteps: Set<EnterpriseTourStep> = new Set();
  currentlyShowing: boolean;
  private tour: Shepherd.Tour;
  private enabled: boolean;

  constructor(private scrollService: ScrollService) {
  }

  start(steps: TourStepSetting[]) {
    if (this.enabled) {
      this.prepareShepherd(steps);
      this.tour.start();
    } else {
      throw new Error('Tour Service must be enabled first once, before starting it.');
    }
  }

  setEnabled(value: boolean) {
    this.enabled = value;
    this.viewedSteps = new Set();
  }

  isEnabled() {
    return this.enabled;
  }

  hideTour() {
    if (this.tour) {
      const currentStep = this.tour.getCurrentStep();
      if (currentStep) {
        currentStep.hide();
      }
    }
  }

  private prepareShepherd(steps: TourStepSetting[]) {
    const isEnvNavTour = steps.some(s => s.classes === 'env-nav-guide');
    const modalContainer = isEnvNavTour ? document.body : (document.getElementById('scrollableContainer') ?? document.body);

    this.tour = new Shepherd.Tour({
      defaultStepOptions: TourConfig.OPTIONS,
      useModalOverlay: true,
      modalContainer
    });
    this.tour.addSteps(this.createShepherdSteps(steps));
    this.tour.on('cancel', this.hideTour.bind(this));
    this.tour.on('complete', this.hideTour.bind(this));
  }

  private createShepherdSteps(settings: TourStepSetting[]): any[] {
    const filtered = settings.filter(s => {
      if (this.viewedSteps.has(s.id)) { return false; }
      if (s.anchorSettings) { return !!document.querySelector(s.anchorSettings.element); }
      return true;
    });
    return filtered.map((setting, index) => this.buildStep(setting, index, filtered.length));
  }

  private buildStep(setting: TourStepSetting, index: number = 0, total: number = 1) {
    const isFirst = index === 0;
    const isLast = index === total - 1;

    const buttons = [];
    if (total > 1) {
      buttons.push({
        classes: 'shepherd-step-label',
        text: `Step ${index + 1} of ${total}`,
        action: () => {}
      });
    }
    if (!isFirst) {
      buttons.push({
        classes: 'shepherd-button-prev',
        text: 'Back',
        action: () => { this.tour.back(); }
      });
    }
    buttons.push({
      classes: isLast ? 'shepherd-button-done' : 'shepherd-button-next',
      text: isLast ? 'End Tour' : 'Next',
      action: () => { isLast ? this.tour.complete() : this.tour.next(); }
    });

    const attachTo = setting.anchorSettings
      ? {
          element: (document.querySelector(setting.anchorSettings.element) as HTMLElement) ?? setting.anchorSettings.element,
          on: setting.anchorSettings.on
        }
      : undefined;

    return {
      id: setting.id,
      classes: setting.classes,
      title: setting.title,
      attachTo,
      beforeShowPromise: setting.anchorSettings
        ? (): Promise<void> => new Promise<void>(resolve => {
            const el = document.querySelector(setting.anchorSettings.element);
            if (el) { el.scrollIntoView({ behavior: 'instant', block: 'start' }); }
            requestAnimationFrame(() => resolve());
          })
        : setting.skipDelay
          ? undefined
          : (): Promise<void> => new Promise<void>(resolve => {
              setTimeout(() => { window.scrollTo(0, 0); resolve(); }, 500);
            }),
      buttons,
      popperOptions: {
        modifiers: [
          {
            name: 'offset',
            options: {offset: setting.offset ? [setting.offset.perpendicular, setting.offset.parallel] : [0, 0]}
          },
          {
            name: 'flip',
            options: {fallbackPlacements: []}
          },
          {
            name: 'preventOverflow',
            options: {padding: 8}
          }
        ]
      },
      canClickTarget: false,
      cancelIcon: {
        enabled: true
      },
      highlightClass: 'highlight',
      modalOverlayOpeningRadius: setting.overlay ? setting.overlay.radius : 0,
      modalOverlayOpeningPadding: setting.overlay ? setting.overlay.padding : 0,
      text: [translate(setting.translateKey)],
      when: {
        show: () => {
          this.currentlyShowing = true;
          this.viewedSteps.add(setting.id);
          this.tourSubject.next({stepId: setting.id, eventType: 'show'});
          this.scrollService.disableScroll();
        },
        hide: () => {
          this.currentlyShowing = false;
          this.tourSubject.next({stepId: setting.id, eventType: 'hide'});
          this.scrollService.enableScroll();
        }
      }
    };
  }
}
